import { useState } from 'react';
import { mormSigner, AgentDelegationResult, KeypairResult } from '../sdk';

/**
 * Custom hook for managing agent delegation operations
 * 
 * Handles the complete agent delegation workflow:
 * - Generating owner address from mnemonic
 * - Generating agent keypair
 * - Preparing agent sign (EIP-712 typed data)
 * - Signing agent approval
 * 
 * @param initialOwnerMnemonic - Initial owner mnemonic phrase (default: standard test mnemonic)
 * @param initialExpiresInDays - Initial expiration days (default: 180)
 * @param initialPermissions - Initial permissions string (default: 'clob::submit_order,clob::cancel_order')
 * @param initialAgentName - Initial agent name (default: 'TradingBot')
 * @returns Object containing:
 *   - ownerMnemonic: Current owner mnemonic phrase
 *   - setOwnerMnemonic: Function to update owner mnemonic
 *   - ownerAddress: Generated owner address
 *   - setOwnerAddress: Function to update owner address
 *   - agentAddress: Generated agent address
 *   - setAgentAddress: Function to update agent address
 *   - agentPrivateKey: Generated agent private key
 *   - setAgentPrivateKey: Function to update agent private key
 *   - expiresInDays: Expiration days for delegation
 *   - setExpiresInDays: Function to update expiration days
 *   - permissions: Permissions string (comma-separated)
 *   - setPermissions: Function to update permissions
 *   - agentName: Agent name
 *   - setAgentName: Function to update agent name
 *   - nonce: Nonce for delegation
 *   - setNonce: Function to update nonce
 *   - result: Delegation result or error
 *   - loading: Boolean indicating if operation is in progress
 *   - generateOwnerAddress: Function to generate owner address from mnemonic
 *   - generateAgentKeypair: Function to generate agent keypair
 *   - prepareAgentSign: Function to prepare agent sign (EIP-712 typed data)
 *   - signAgentApproval: Function to sign agent approval
 *   - isReady: Boolean indicating if mormSigner is ready
 */
export function useAgentDelegation(
  initialOwnerMnemonic: string = 'abandon abandon abandon abandon abandon abandon abandon abandon abandon abandon abandon about',
  initialExpiresInDays: number = 180,
  initialPermissions: string = 'clob::submit_order,clob::cancel_order',
  initialAgentName: string = 'TradingBot'
) {
  const [ownerMnemonic, setOwnerMnemonic] = useState<string>(initialOwnerMnemonic);
  const [ownerAddress, setOwnerAddress] = useState<string>('');
  const [agentAddress, setAgentAddress] = useState<string>('');
  const [agentPrivateKey, setAgentPrivateKey] = useState<string>('');
  const [expiresInDays, setExpiresInDays] = useState<number>(initialExpiresInDays);
  const [permissions, setPermissions] = useState<string>(initialPermissions);
  const [agentName, setAgentName] = useState<string>(initialAgentName);
  const [nonce, setNonce] = useState<string>(Date.now().toString());
  const [result, setResult] = useState<AgentDelegationResult | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const generateOwnerAddress = async (): Promise<void> => {
    if (!mormSigner.isReady()) {
      setResult({
        success: false,
        error: 'WASM module not ready'
      });
      return;
    }

    setLoading(true);
    try {
      const addressResult = await mormSigner.generateAddress(ownerMnemonic, 0);
      if (addressResult.success && addressResult.address) {
        setOwnerAddress(addressResult.address);
      } else {
        setResult({
          success: false,
          error: addressResult.error || 'Failed to generate owner address'
        });
      }
    } catch (error) {
      setResult({
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      });
    } finally {
      setLoading(false);
    }
  };

  const generateAgentKeypair = async (): Promise<void> => {
    if (!mormSigner.isReady()) {
      setResult({
        success: false,
        error: 'WASM module not ready'
      });
      return;
    }

    setLoading(true);
    try {
      const keypairResult: KeypairResult = await mormSigner.generateKeypair();
      if (keypairResult.success && keypairResult.address && keypairResult.privateKey) {
        setAgentAddress(keypairResult.address);
        setAgentPrivateKey(keypairResult.privateKey);
      } else {
        setResult({
          success: false,
          error: keypairResult.error || 'Failed to generate agent keypair'
        });
      }
    } catch (error) {
      setResult({
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      });
    } finally {
      setLoading(false);
    }
  };

  const prepareAgentSign = async (): Promise<void> => {
    if (!mormSigner.isReady()) {
      setResult({
        success: false,
        error: 'WASM module not ready'
      });
      return;
    }

    if (!ownerAddress || !agentAddress) {
      setResult({
        success: false,
        error: 'Owner and agent addresses are required'
      });
      return;
    }

    setLoading(true);
    setResult(null);

    try {
      const permissionsArray = permissions.split(',').map(p => p.trim()).filter(p => p.length > 0);
      
      const result = await mormSigner.prepareAgentSign({
        owner: ownerAddress,
        agent: agentAddress,
        expiresInDays: expiresInDays,
        permissions: permissionsArray,
        nonce: nonce,
        agentName: agentName
      });
      
      setResult(result);
    } catch (error) {
      setResult({
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      });
    } finally {
      setLoading(false);
    }
  };

  const signAgentApproval = async (): Promise<void> => {
    if (!mormSigner.isReady()) {
      setResult({
        success: false,
        error: 'WASM module not ready'
      });
      return;
    }

    if (!ownerAddress || !agentAddress || !ownerMnemonic) {
      setResult({
        success: false,
        error: 'Owner address, agent address, and owner mnemonic are required'
      });
      return;
    }

    setLoading(true);
    setResult(null);

    try {
      const permissionsArray = permissions.split(',').map(p => p.trim()).filter(p => p.length > 0);
      
      const result = await mormSigner.signAgentApproval(ownerMnemonic, {
        owner: ownerAddress,
        agent: agentAddress,
        expiresInDays: expiresInDays,
        permissions: permissionsArray,
        nonce: nonce,
        agentName: agentName
      });
      
      setResult(result);
    } catch (error) {
      setResult({
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      });
    } finally {
      setLoading(false);
    }
  };

  return {
    ownerMnemonic,
    setOwnerMnemonic,
    ownerAddress,
    setOwnerAddress,
    agentAddress,
    setAgentAddress,
    agentPrivateKey,
    setAgentPrivateKey,
    expiresInDays,
    setExpiresInDays,
    permissions,
    setPermissions,
    agentName,
    setAgentName,
    nonce,
    setNonce,
    result,
    loading,
    generateOwnerAddress,
    generateAgentKeypair,
    prepareAgentSign,
    signAgentApproval,
    isReady: mormSigner.isReady()
  };
}
